import React from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Divider, Image, Menu } from "semantic-ui-react";
import { Link } from "react-router-dom";
import TourNav from "../../constants/TourNav";
import "../../styles/TourMenu.less";
import TourAcl from "~/models/Tour/TourAcl";
import User from "~/models/User";
import AuthenticatedUserHelper from "~/utils/AuthenticatedUserHelper";

const TourMenu = observer(({ tourId, active, tourAcl, user, token, t }) => (
  <Menu pointing secondary vertical fluid>
    <div className="tour-layout-menu-container">
      {(tourAcl.info || tourAcl.download || tourAcl.address || tourAcl.mls || tourAcl.settings) && (
        <Menu.Header content={<b>{t("general")}</b>} />
      )}
      {tourAcl.download && (
        <Menu.Item
          disabled={tourAcl.ransomReview}
          icon="cloud download"
          as={tourAcl.ransomReview ? "span" : Link}
          to={
            AuthenticatedUserHelper.isAuthenticated(user)
              ? `/tours/${tourId}/download`
              : `/tours/${tourId}/download?token=${token}`
          }
          content={t("clientPanelMisc:downloadCenter")}
          active={active === TourNav.DOWNLOAD}
        />
      )}
      {/* Access only if authenticated as Customer */}
      {AuthenticatedUserHelper.isAuthenticatedCustomer(user) && (
        <>
          {tourAcl.info && (
            <Menu.Item
              disabled={tourAcl.ransomReview}
              icon="info circle"
              as={tourAcl.ransomReview ? "span" : Link}
              to={`/tours/${tourId}/info`}
              content={t("clientPanelMisc:info")}
              active={active === TourNav.INFO}
            />
          )}
          {tourAcl.address && (
            <Menu.Item
              disabled={tourAcl.ransomReview}
              icon="map marker alternate"
              as={tourAcl.ransomReview ? "span" : Link}
              to={`/tours/${tourId}/address`}
              content={t("clientPanelMisc:address")}
              // Note this is the default tour page, you can navigate by /tours/{tourId}/address or just /tours/{tourId}
              // Feel free to make another page the default one.
              active={active === TourNav.ADDRESS || active === ""}
            />
          )}
          {tourAcl.mls && (
            <Menu.Item
              disabled={tourAcl.ransomReview}
              icon="cog"
              as={tourAcl.ransomReview ? "span" : Link}
              to={`/tours/${tourId}/mls`}
              content={t("clientPanelMisc:mls")}
              active={active === TourNav.MLS}
            />
          )}
          {tourAcl.settings && (
            <Menu.Item
              disabled={tourAcl.ransomReview}
              icon="wrench"
              as={tourAcl.ransomReview ? "span" : Link}
              to={`/tours/${tourId}/settings`}
              content={t("clientPanelMisc:settings")}
              active={active === TourNav.SETTINGS}
            />
          )}
          {tourAcl.immersive3dTour && (
            // Notice that this Menu.Item is not the shorthand version because if we use "Immersive 3D Tour" in its name
            // property, then it will render like: "Immersive 3 D Tour" (a space between the 3 and the D, for some reason)
            <Menu.Item
              disabled={tourAcl.ransomReview}
              as={tourAcl.ransomReview ? "span" : Link}
              to={`/tours/${tourId}/immersive-3d-tour`}
              active={active === TourNav.IMMERSIVE_3D_TOUR}
            >
              <Image className="icon customIcon" src="/assets/images/trivaxis_sigil.svg" />
              {t("immersive3dTour")}
            </Menu.Item>
          )}
          {tourAcl.uiMeet3d && (
            <Menu.Item
              disabled={tourAcl.ransomReview}
              as={tourAcl.ransomReview ? "span" : Link}
              to={`/tours/${tourId}/ui-meet-3d`}
              active={active === TourNav.UI_MEET_3D}
            >
              <Image className="icon customIcon" src="/assets/images/trivaxis_sigil.svg" />
              {t("uiMeet3d")}
            </Menu.Item>
          )}
          {(tourAcl.mediaEdit || tourAcl.mediaSettings || tourAcl.videoRequest || tourAcl.flyer) && (
            <>
              <Menu.Item disabled={tourAcl.ransomReview} content={<Divider />} />
              <Menu.Header content={<b>{t("clientPanelMisc:media")}</b>} />
            </>
          )}
          {tourAcl.mediaEdit && (
            <Menu.Item
              disabled={tourAcl.ransomReview}
              icon="image"
              as={tourAcl.ransomReview ? "span" : Link}
              to={`/tours/${tourId}/media-edit`}
              content={t("clientPanelMisc:mediaEdit")}
              active={active === TourNav.MEDIA_EDIT}
            />
          )}
          {tourAcl.mediaSettings && (
            <Menu.Item
              disabled={tourAcl.ransomReview}
              icon="sliders"
              as={tourAcl.ransomReview ? "span" : Link}
              to={`/tours/${tourId}/media-settings`}
              content={t("clientPanelMisc:mediaSettings")}
              active={active === TourNav.MEDIA_SETTINGS}
            />
          )}
          {tourAcl.videoRequest && (
            <Menu.Item
              disabled={tourAcl.ransomReview}
              icon="video"
              as={tourAcl.ransomReview ? "span" : Link}
              to={`/tours/${tourId}/video-request`}
              content={t("clientPanelMisc:videoRequest")}
              active={active === TourNav.VIDEO}
            />
          )}
          {tourAcl.flyer && (
            <Menu.Item
              disabled={tourAcl.ransomReview}
              icon="file alternate"
              as={tourAcl.ransomReview ? "span" : Link}
              to={`/tours/${tourId}/flyer`}
              content={t("clientPanelMisc:flyerEdit")}
              active={active === TourNav.FLYER}
            />
          )}
          {(tourAcl.stats || tourAcl.links) && (
            <>
              <Menu.Item disabled={tourAcl.ransomReview} content={<Divider />} />
              <Menu.Header content={<b>{t("clientPanelMisc:misc")}</b>} />
            </>
          )}
          {tourAcl.stats && (
            <Menu.Item
              disabled={tourAcl.ransomReview}
              icon="chart bar"
              as={tourAcl.ransomReview ? "span" : Link}
              to={`/tours/${tourId}/stats`}
              content={t("clientPanelMisc:stats")}
              active={active === TourNav.STATS}
            />
          )}
          {tourAcl.links && (
            <Menu.Item
              disabled={tourAcl.ransomReview}
              icon="linkify"
              as={tourAcl.ransomReview ? "span" : Link}
              to={`/tours/${tourId}/links`}
              content={t("clientPanelMisc:links")}
              active={active === TourNav.LINKS}
            />
          )}
        </>
      )}
    </div>
  </Menu>
));

TourMenu.propTypes = {
  tourId: PropTypes.number.isRequired,
  active: PropTypes.string,
  tourAcl: PropTypes.instanceOf(TourAcl).isRequired,
  user: PropTypes.instanceOf(User),
  token: PropTypes.string,
  t: PropTypes.func.isRequired,
};

TourMenu.defaultProps = {
  active: "",
  token: null,
};

export default withTranslation()(TourMenu);
